import { useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { AddressAutocomplete } from "../components/AddressAutocomplete";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { PrivacyNotice } from "@/components/PrivacyNotice";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { reasonsFor, labelCase, isFloorBranch, DEPARTMENTS, deptLabel } from "@/lib/queueConfig";
import { useQueueTicket } from "@/lib/useQueueTicket";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const emailOk = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((e || "").trim());

const Field = ({ label, children }) => (
  <div>
    <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
      {label}
    </Label>
    <div className="mt-1.5">{children}</div>
  </div>
);

/** Public landing page behind the counter QR code — one form that joins the queue
 *  and registers the customer at the same time. No viewing or editing of anyone's
 *  stored details here, so nobody else's information is ever exposed. */
export default function VisitLanding() {
  const { branch } = useParams();
  const store = (branch || "").toUpperCase();
  const showFloor = isFloorBranch(store);
  const reasons = reasonsFor(store);
  const [form, setForm] = useState({
    phone: "",
    name: "",
    address: "",
    email: "",
    birthday: "",
    department: "",
    reason: "ENQUIRY",
    note: "",
    party_size: 1,
  });
  const [privacy, setPrivacy] = useState(false);
  const [joinTicket, setJoinTicket] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);

  // The ticket is remembered on this phone (and polled for a live "people ahead of you"),
  // so a page refresh keeps the customer on their number instead of a blank join form.
  const { ticket, live, setTicket, leaveQueue } = useQueueTicket(store);

  const fail = (err, fallback) =>
    toast.error(
      (err.response?.data?.detail && String(err.response.data.detail)) || fallback
    );

  const post = async (payload) => {
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/public/queue`, {
        ...form,
        ...payload,
        branch: store,
      });
      setTicket(data);
    } catch (err) {
      fail(err, "Could not add you to the queue");
    } finally {
      setSaving(false);
    }
  };

  const joinNew = () => {
    if (showFloor && !form.department)
      return toast.error("Please choose Gold or Silver so we send you to the right floor");
    if (!form.phone.trim()) return toast.error("Please enter your phone number");
    if (!form.name.trim()) return toast.error("Please enter your full name");
    if (!form.address.trim()) return toast.error("Please enter your address");
    if (!emailOk(form.email)) return toast.error("Please enter a valid email address");
    if (!privacy)
      return toast.error("Please tick the box to confirm you've read the privacy notice");
    return post({ privacy_accepted: true });
  };

  const joinGroup = () => {
    if (!form.phone.trim() || !form.name.trim())
      return toast.error("Please enter your name and phone number");
    if (!joinTicket.trim()) return toast.error("Enter your group's ticket number");
    return post({ join_ticket: Number(joinTicket) });
  };

  return (
    <div className="min-h-screen bg-[#FBF8F2] px-4 py-10">
      <div className="mx-auto max-w-md border border-border bg-white p-6 sm:p-8">
        <div className="border-b border-border pb-4 text-center">
          <p className="font-invoice text-xl font-bold tracking-wide">DHUNNA JEWELLERS</p>
          <p className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
            {store} store
          </p>
        </div>

        {ticket ? (
          <div className="py-8 text-center" data-testid="queue-ticket">
            <CheckCircle2 className="mx-auto h-11 w-11 text-[#166534]" />
            <p className="mt-3 text-xs uppercase tracking-[0.2em] text-muted-foreground">
              You're in the queue — ticket
            </p>
            <p className="tabular mt-1 text-6xl font-bold" data-testid="queue-ticket-number">
              {ticket.ticket}
            </p>
            {ticket.department && (
              <p
                className="mt-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#8A6C36]"
                data-testid="queue-ticket-dept"
              >
                {deptLabel(ticket.department)}
              </p>
            )}

            {live && (live.status === "serving" || live.ahead === 0) ? (
              <div
                className="mt-6 border border-[#166534] bg-[#F0FBF3] px-4 py-5"
                data-testid="queue-live-turn"
              >
                <p className="text-lg font-bold text-[#166534]">
                  {live.status === "serving" ? "It's your turn" : "You're next"}
                </p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Please come to the {ticket.department ? `${deptLabel(ticket.department)} counter` : "counter"}.
                </p>
              </div>
            ) : (
              <div
                className="mt-6 border border-border bg-[#FBF8F2] px-4 py-5"
                data-testid="queue-live-ahead"
              >
                <p className="tabular text-4xl font-bold text-[#0A0A0A]">
                  {live ? live.ahead : "—"}
                </p>
                <p className="mt-1 text-xs uppercase tracking-[0.2em] text-muted-foreground">
                  {live && live.ahead === 1 ? "person ahead of you" : "people ahead of you"}
                  {ticket.department ? " on your floor" : ""}
                </p>
                {live && (live.now_serving || []).length > 0 && (
                  <p className="mt-3 text-sm text-muted-foreground" data-testid="queue-live-serving">
                    Now serving #{live.now_serving.join(", #")}
                  </p>
                )}
                {live && live.est_wait_min > 0 && (
                  <p className="mt-1 text-sm text-muted-foreground" data-testid="queue-live-wait">
                    Estimated wait: about {live.est_wait_min} min
                  </p>
                )}
              </div>
            )}

            <p className="mt-4 text-sm text-muted-foreground">
              Feel free to browse while you wait — this page updates on its own.
            </p>
            {(ticket.party_size || 1) > 1 && (
              <p
                className="mt-2 text-xs uppercase tracking-wider text-[#8A6C36]"
                data-testid="queue-ticket-party"
              >
                One ticket for your party of {ticket.party_size}
              </p>
            )}
            {confirmLeave ? (
              <div className="mt-6 flex flex-col items-center gap-2" data-testid="queue-leave-confirm-row">
                <p className="text-xs uppercase tracking-wider text-[#8A6C36]">
                  Leave and give up your spot?
                </p>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={async () => {
                      await leaveQueue();
                      setConfirmLeave(false);
                      toast.success("You've left the queue");
                    }}
                    className="border border-[#B24A3A]/50 px-4 py-2 text-xs font-bold uppercase tracking-wider text-[#B24A3A] hover:bg-[#FBEDE8]"
                    data-testid="queue-leave-confirm"
                  >
                    Yes, leave
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmLeave(false)}
                    className="border border-[#8A6C36]/40 px-4 py-2 text-xs font-bold uppercase tracking-wider text-[#8A6C36] hover:bg-[#F4EFE0]"
                    data-testid="queue-leave-cancel"
                  >
                    Keep my spot
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmLeave(true)}
                className="mt-6 text-xs uppercase tracking-wider text-[#8A6C36] underline-offset-2 hover:underline"
                data-testid="queue-leave"
              >
                Leave the queue
              </button>
            )}
          </div>
        ) : (
          <div className="mt-6 space-y-4" data-testid="queue-form">
            <p className="text-sm text-muted-foreground">
              Join the queue and we'll call you by number. Your details are saved so we
              don't have to ask again next time.
            </p>
            {showFloor && (
              <div data-testid="queue-dept-step">
                <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Which are you here for? *
                </Label>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {DEPARTMENTS.map((d) => (
                    <button
                      key={d.key}
                      type="button"
                      onClick={() => setForm({ ...form, department: d.key })}
                      className={`border px-3 py-3 text-left transition-colors duration-150 ${
                        form.department === d.key
                          ? "border-[#C5A059] bg-[#FBF8F2]"
                          : "border-border"
                      }`}
                      data-testid={`queue-dept-${d.key}`}
                    >
                      <span className="block text-sm font-semibold">{d.label}</span>
                      <span className="block text-xs text-muted-foreground">{d.floor}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <Field label="Phone number *">
              <Input
                className="h-11 rounded-none"
                inputMode="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                data-testid="queue-phone-input"
              />
            </Field>
            <Field label="Full name *">
              <Input
                className="h-11 rounded-none"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="queue-name-input"
              />
            </Field>
            <Field label="Address *">
              <AddressAutocomplete
                className="h-11 rounded-none"
                value={form.address}
                onChange={(v) => setForm({ ...form, address: v })}
                placeholder="Start typing your address"
                testid="queue-address-input"
              />
            </Field>
            <Field label="Email address *">
              <Input
                className="h-11 rounded-none"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="queue-email-input"
              />
            </Field>
            <Field label="Birthday (optional — for a birthday offer)">
              <Input
                className="h-11 rounded-none"
                type="date"
                value={form.birthday}
                onChange={(e) => setForm({ ...form, birthday: e.target.value })}
                data-testid="queue-birthday-input"
              />
            </Field>

            <div>
              <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                What can we help with?
              </Label>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {reasons.map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setForm({ ...form, reason: r })}
                    className={`border px-3 py-2.5 text-sm transition-colors duration-150 ${
                      form.reason === r
                        ? "border-[#C5A059] bg-[#FBF8F2] font-semibold"
                        : "border-border"
                    }`}
                    data-testid={`queue-reason-${r.replace(/[^A-Za-z0-9]+/g, "-")}`}
                  >
                    {labelCase(r)}
                  </button>
                ))}
              </div>
            </div>

            <Field label="Anything we should know? (optional)">
              <Input
                className="h-11 rounded-none"
                placeholder="e.g. ring resize, size 7"
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                data-testid="queue-note-input"
              />
            </Field>

            <div>
              <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                How many people are with you?
              </Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setForm({ ...form, party_size: n })}
                    className={`h-11 w-12 border text-sm transition-colors duration-150 ${
                      form.party_size === n
                        ? "border-[#C5A059] bg-[#FBF8F2] font-semibold"
                        : "border-border"
                    }`}
                    data-testid={`queue-party-${n}`}
                  >
                    {n === 5 ? "5+" : n}
                  </button>
                ))}
              </div>
              <p className="mt-1.5 text-xs text-muted-foreground">
                One ticket covers your whole group — no need for everyone to scan.
              </p>
            </div>

            <PrivacyNotice
              branch={branch}
              accepted={privacy}
              onChange={setPrivacy}
              testid="queue-privacy"
            />
            <Button
              className="w-full rounded-none bg-[#C5A059] py-6 font-semibold text-[#0A0A0A] hover:bg-[#B38F4E]"
              onClick={joinNew}
              disabled={saving}
              data-testid="queue-submit-button"
            >
              {saving ? "Adding you…" : "Join the queue"}
            </Button>

            <div className="border-t border-border pt-4">
              <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Someone in my group already joined
              </Label>
              <div className="mt-2 flex gap-2">
                <Input
                  className="h-11 rounded-none"
                  placeholder="Their ticket number"
                  inputMode="numeric"
                  value={joinTicket}
                  onChange={(e) => setJoinTicket(e.target.value)}
                  data-testid="queue-join-ticket-input"
                />
                <Button
                  variant="outline"
                  className="h-11 whitespace-nowrap rounded-none"
                  onClick={joinGroup}
                  disabled={saving}
                  data-testid="queue-join-ticket-button"
                >
                  Join them
                </Button>
              </div>
              <p className="mt-1.5 text-xs text-muted-foreground">
                Just enter your name and phone above, then their ticket number.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
